import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const AuthCtx = createContext(null);
const IDLE_MS = 30 * 60 * 1000; // 30 minutes

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [error, setError] = useState("");
  const idleTimerRef = useRef(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      return r.data;
    } catch (err) {
      if (err.response?.status !== 401) console.error("Auth refresh failed:", err);
      setUser(false);
      return null;
    }
  }, []);

  const doLogout = useCallback(async (idle = false) => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // 401 é esperado se sessão já expirou; demais erros logamos.
      if (err.response?.status !== 401) console.error("Logout request failed:", err);
    }
    setUser(false);
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (idle) toast.info("Sessão encerrada por inatividade (30 minutos).");
  }, []);

  useEffect(() => {
    if (!user) return;
    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    const reset = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => doLogout(true), IDLE_MS);
    };
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [user, doLogout]);

  async function login(email, password, remember_me = false) {
    try {
      const { data } = await api.post("/auth/login", { email, password, remember_me });
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  async function register(payload) {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return data;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setError(msg);
      throw new Error(msg);
    }
  }

  const logout = useCallback(() => doLogout(false), [doLogout]);

  return (
    <AuthCtx.Provider value={{ user, login, register, logout, refresh, error, setError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
